/**
 * Labstep
 *
 * @module rtk/services/global-search
 * @desc Query the API for global search using RTK Query
 */

import {
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { plainToInstance } from 'class-transformer';
import { GlobalSearchResult } from 'labstep-web/models/global-search-result.model';
import { configService } from 'labstep-web/services/config.service';
import { selectAuthenticatedUser } from 'labstep-web/state/selectors/authenticated-user';
import type { LabstepReduxState } from 'labstep-web/state/types';

export const searchApi = createApi({
  reducerPath: 'searchApi',
  baseQuery: fetchBaseQuery({
    baseUrl: configService.labstepApiUrl,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as LabstepReduxState;
      const authenticatedUser = selectAuthenticatedUser(state);

      if (authenticatedUser?.token) {
        headers.set(
          'Authorization',
          `Bearer ${authenticatedUser.token}`,
        );
      }

      return headers;
    },
  }),
  tagTypes: ['Search'],
  endpoints: (builder) => ({
    search: builder.query<GlobalSearchResult[], string>({
      query: (searchQuery) =>
        `api/generic/search/${encodeURIComponent(searchQuery)}`,
      transformResponse: (response: any[]): GlobalSearchResult[] => {
        return plainToInstance(GlobalSearchResult, response);
      },
      providesTags: (result, error, searchQuery) => [
        { type: 'Search', id: searchQuery },
      ],
      // Cache search results for 1 minute (60 seconds)
      keepUnusedDataFor: 60,
    }),
  }),
});

export const { useSearchQuery, useLazySearchQuery } = searchApi;
